import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';

/**
 * Year-End Closing Wizard — Phase 2.
 *
 * 4-step flow keyed off the parent FY's status:
 *   open     → Step 1: Preview snapshot (read-only, "Start Close")
 *   closing  → Step 2: Edit opening balances (per-row inline) + "Finalize"
 *   closed   → Step 3: Locked snapshot summary + admin "Reopen" button
 *
 * The wizard is intentionally heavy-data: each section (Receivables /
 * Customer Advances / Payables / Inventory) is a collapsible table the
 * accountant scrolls through. Edits hit PUT /snapshot/edit and the section
 * total is recomputed server-side and returned in the response so the UI
 * stays consistent without a full reload.
 */
const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

const SECTION_META = {
  receivables: {
    title: '🧾 Receivables (Customers Owe Us)',
    rowKey: 'customer_id',
    nameKey: 'customer_name',
    valueKey: 'outstanding_bhd',
    extraCol: { label: 'Invoices', key: 'invoice_count' },
    totalKey: 'total_bhd',
    countKey: 'customer_count',
    tone: 'emerald',
  },
  customer_advances: {
    title: '💰 Customer Advances (Unapplied Receipts)',
    rowKey: 'customer_id',
    nameKey: 'customer_name',
    valueKey: 'advance_bhd',
    extraCol: { label: 'Receipts', key: 'receipt_count' },
    totalKey: 'total_bhd',
    countKey: 'customer_count',
    tone: 'amber',
  },
  payables: {
    title: '🏭 Payables (We Owe Suppliers)',
    rowKey: 'supplier_id',
    nameKey: 'supplier_name',
    valueKey: 'outstanding_bhd',
    extraCol: { label: 'Entries', key: 'entry_count' },
    totalKey: 'total_bhd',
    countKey: 'supplier_count',
    tone: 'rose',
  },
  inventory: {
    title: '📦 Inventory Value (Stock on Hand)',
    rowKey: 'product_id',
    nameKey: 'product_name',
    valueKey: 'value_bhd',
    extraCol: { label: 'Qty', key: 'qty' },
    totalKey: 'total_value_bhd',
    countKey: null,
    tone: 'sky',
  },
};

const toneClass = (tone) => {
  const map = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    sky: 'border-sky-200 bg-sky-50',
  };
  return map[tone] || 'border-gray-200 bg-gray-50';
};

const Section = ({ kind, data, readOnly, onEditValue }) => {
  const meta = SECTION_META[kind];
  if (!meta) return null;
  const rows = data?.rows || [];
  const total = data?.[meta.totalKey] ?? 0;
  const count = meta.countKey ? data?.[meta.countKey] ?? 0 : rows.length;
  return (
    <div
      className={`border rounded-lg p-3 ${toneClass(meta.tone)}`}
      data-testid={`fy-close-section-${kind}`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">{meta.title}</h3>
        <div className="text-xs text-gray-600">
          {count} {kind === 'inventory' ? `products${data?.total_units ? ` · ${data.total_units} units` : ''}` : 'records'}
          {' · Total '}
          <span className="font-semibold text-gray-900" data-testid={`fy-close-${kind}-total`}>
            {fmt(total)}
          </span>{' BHD'}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500 italic">No entries.</p>
      ) : (
        <div className="max-h-60 overflow-y-auto bg-white rounded border">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1">{kind === 'payables' ? 'Supplier' : kind === 'inventory' ? 'Product' : 'Customer'}</th>
                <th className="text-right px-2 py-1 w-20">{meta.extraCol.label}</th>
                <th className="text-right px-2 py-1 w-32">Amount (BHD)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r[meta.rowKey]} className="border-t">
                  <td className="px-2 py-1">{r[meta.nameKey] || r[meta.rowKey]}</td>
                  <td className="px-2 py-1 text-right">{r[meta.extraCol.key] ?? 0}</td>
                  <td className="px-2 py-1 text-right">
                    {readOnly ? (
                      <span className="font-mono">{fmt(r[meta.valueKey])}</span>
                    ) : (
                      <input
                        type="number"
                        step="0.001"
                        defaultValue={r[meta.valueKey]}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (!Number.isFinite(v)) return;
                          if (Math.abs(v - Number(r[meta.valueKey] || 0)) < 0.0005) return;
                          onEditValue(kind, r[meta.rowKey], v);
                        }}
                        className="w-28 text-right border rounded px-1 py-0.5 font-mono"
                        data-testid={`fy-close-edit-${kind}-${r[meta.rowKey]}`}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const YearEndCloseWizard = ({ fy, onClose, onChanged }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isOpen = fy.status === 'open';
  const isClosing = fy.status === 'closing';
  const isClosed = fy.status === 'closed';

  const loadSnapshot = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      if (isOpen) {
        const res = await api.get(`/fiscal-years/${fy.id}/close-preview`);
        setSnapshot(res.data);
      } else {
        const res = await api.get(`/fiscal-years/${fy.id}/snapshot`);
        // For closing/closed states the snapshot has both auto_snapshot
        // and edited_snapshot — surface edited as the working copy.
        setSnapshot(res.data.edited_snapshot || res.data.auto_snapshot);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load snapshot.');
    } finally {
      setLoading(false);
    }
  }, [fy.id, isOpen]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  const onStartClose = async () => {
    if (!window.confirm(`Start closing ${fy.name}? You will then be able to edit opening balances before finalizing.`)) return;
    setError('');
    setBusy(true);
    try {
      await api.post(`/fiscal-years/${fy.id}/start-close`);
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to start close.');
    } finally {
      setBusy(false);
    }
  };

  const onEditValue = async (section, rowKey, value) => {
    setError('');
    try {
      const res = await api.put(`/fiscal-years/${fy.id}/snapshot/edit`, {
        section,
        row_key: rowKey,
        value,
      });
      if (res.data?.edited_snapshot) setSnapshot(res.data.edited_snapshot);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save edit.');
    }
  };

  const onFinalize = async () => {
    if (!window.confirm(`Finalize close for ${fy.name}? This locks the year — non-admin users will no longer be able to edit records dated within this FY.`)) return;
    setError('');
    setBusy(true);
    try {
      await api.post(`/fiscal-years/${fy.id}/finalize-close`);
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to finalize.');
    } finally {
      setBusy(false);
    }
  };

  const onReopen = async () => {
    if (!window.confirm(`Reopen ${fy.name}? Opening balances will be cleared and re-snapshotted on the next close.`)) return;
    setError('');
    setBusy(true);
    try {
      await api.post(`/fiscal-years/${fy.id}/reopen`);
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to reopen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-3" data-testid="fy-close-wizard">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Year-End Closing · {fy.name}
            </h2>
            <p className="text-xs text-gray-500">
              As of {fy.end_date}{' '}
              <span className="ml-2 uppercase text-[10px] font-bold">
                {isOpen && <span className="text-blue-700">Step 1 · Preview</span>}
                {isClosing && <span className="text-amber-700">Step 2 · Edit balances</span>}
                {isClosed && <span className="text-emerald-700">Step 3 · Closed</span>}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-sm px-3 py-1 border rounded hover:bg-gray-50"
            data-testid="fy-close-wizard-close"
          >
            Close
          </button>
        </div>

        <div className="px-4 py-3 overflow-y-auto flex-1 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3" data-testid="fy-close-error">
              {error}
            </div>
          )}
          {loading ? (
            <p className="text-sm text-gray-500">Loading snapshot…</p>
          ) : !snapshot ? (
            <p className="text-sm text-gray-500">No snapshot available.</p>
          ) : (
            <>
              <Section kind="receivables" data={snapshot.receivables} readOnly={!isClosing} onEditValue={onEditValue} />
              <Section kind="customer_advances" data={snapshot.customer_advances} readOnly={!isClosing} onEditValue={onEditValue} />
              <Section kind="payables" data={snapshot.payables} readOnly={!isClosing} onEditValue={onEditValue} />
              <Section kind="inventory" data={snapshot.inventory} readOnly={!isClosing} onEditValue={onEditValue} />
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-gray-500">
            {isClosing ? 'Click any amount to edit. Section totals recompute live.' : isClosed ? 'Snapshot locked. Admin can Reopen to revise.' : 'Preview only — click Start Close to enter edit mode.'}
          </div>
          <div className="flex items-center gap-2">
            {isOpen && (
              <button
                onClick={onStartClose}
                disabled={busy || loading}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
                data-testid="fy-close-start-btn"
              >
                {busy ? 'Starting…' : 'Start Close →'}
              </button>
            )}
            {isClosing && (
              <button
                onClick={onFinalize}
                disabled={busy || loading}
                className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
                data-testid="fy-close-finalize-btn"
              >
                {busy ? 'Finalizing…' : 'Finalize Close 🔒'}
              </button>
            )}
            {isClosed && (
              <button
                onClick={onReopen}
                disabled={busy || loading}
                className="px-3 py-1.5 rounded border border-amber-500 text-amber-700 text-sm font-medium hover:bg-amber-50 disabled:opacity-60"
                data-testid="fy-close-reopen-btn"
              >
                {busy ? 'Reopening…' : 'Reopen Year ↻'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default YearEndCloseWizard;
